import { useEffect, useRef, useState, type ReactNode } from "react";
import { eventById, isFinished, rulesFor, scoreProblem } from "../../../engine/scoring";
import { bountiesOn, curseName, cursesNow, twistNow } from "../../../engine/bench";
import { aliasOf, allScoresIn, availablePlayers, currentRound, displayName, owedScores, roundByNo } from "../../../engine/selectors";
import { TAKEN_BACK } from "../../../engine/state";
import { gamesDone } from "../../../engine/winddown";
import type { CurseKind, GameRecord, Player, PlayerId, Score, TournamentState } from "../../../engine/types";
import { Mascot } from "../components/Mascot";
import { useLook } from "../lib/look";
import { toggleFullscreen, useFullscreen, useWakeLock } from "../lib/screenTools";
import { useTournament, type Screen } from "../lib/useTournament";
import { STATUS_WORDS } from "../lib/transport";
import { loadJson, mmss, msLeft, saveJson, useTick } from "./court/clock";
import { eventChanges } from "./court/eventText";
import { Stepper } from "./court/Stepper";
import { Team, type Person } from "./court/Who";
import { BREAK_OVER, breakNow, countdown } from "../../../shared/day";
import { demoCourts, demoCourtsFromLocation } from "./projector/demoCourts";
import { demoDay, demoDayFromLocation } from "./projector/demoDay";
import "./Court.css";

/** The numbers being entered for one round; capped null = follow the clock. */
interface Draft {
  a: number;
  b: number;
  capped: boolean | null;
}

/** What the tablet keeps in localStorage so a reload mid-entry loses nothing. */
interface Saved {
  /** The round being entered by hand (an owed one), else null for the current round. */
  focus: number | null;
  drafts: Record<string, Draft>;
  /** A score sent and not yet acknowledged. */
  pending: { id: string; roundNo: number; score: Score } | null;
  /** Why the last send was refused, shown over the reopened steppers. */
  reason: string | null;
  /**
   * A score this tablet sent that was not taken because the game already had another one (an
   * admin entered it while the tablet was offline): said under the band until the next round
   * starts (`rounds`: how many rounds there were then) or this tablet sends again.
   */
  clash?: { text: string; rounds: number } | null;
}

const EMPTY: Saved = { focus: null, drafts: {}, pending: null, reason: null, clash: null };
const BLANK: Draft = { a: 0, b: 0, capped: null };

function names(state: TournamentState, ids: readonly string[]): string[] {
  return ids.map((id) => displayName(state.players[id], "tablet"));
}

type FaceOf = (player: Player | undefined) => string | null;

/** A curse on the tablet: its name, or for Solo who plays alone ("Solo: Luke alone"). */
function curseLabel(state: TournamentState, pair: readonly PlayerId[], curse: { kind?: CurseKind; stepsOff?: PlayerId }): string {
  const stays = curse.stepsOff ? pair.find((id) => id !== curse.stepsOff) : undefined;
  return stays ? `Solo: ${displayName(state.players[stays], "tablet")} alone` : curseName(curse);
}

/** Each player as the tablet shows them: the real name, the wall's name beneath, the picture. */
function people(state: TournamentState, faceOf: FaceOf, ids: readonly string[]): Person[] {
  return ids.map((id) => {
    const p = state.players[id];
    return { name: displayName(p, "tablet"), alias: aliasOf(p), src: faceOf(p) };
  });
}

function phaseLine(state: TournamentState, now: number): { label: string; time: string } {
  const left = msLeft(state, now);
  const round = state.clock.roundNo;
  // paused mid-game the frozen round clock is worth seeing; paused between rounds there is no
  // clock to show (a lunch then put two clocks on the tablet, the final-day run of 18 Oct)
  if (state.pausedAll) return { label: "Paused", time: state.clock.phase === "playing" && left !== null ? mmss(left) : "" };
  if (state.clock.phase === "playing") return { label: `Round ${round}`, time: left !== null ? mmss(left) : "" };
  // with rounds started by an admin (decision 41) the changeover is not a count to the next round
  if (state.clock.phase === "changeover" && !state.config.autoAdvance) return { label: "Between rounds", time: "" };
  if (state.clock.phase === "changeover") return { label: "Changeover", time: left !== null ? mmss(left) : "" };
  if (!state.rounds.length) return { label: "Before round 1", time: "" };
  return { label: "Between rounds", time: "" };
}

export function CourtView({ screen }: { screen: Screen }) {
  const t = useTournament(screen);
  const n = screen.court ?? 1;
  const key = `bd.court.${screen.tournament}.${n}`;
  const [saved, setSavedState] = useState<Saved>(() => ({ ...EMPTY, ...loadJson<Partial<Saved>>(key, {}) }));
  const [confirming, setConfirming] = useState(false);
  useTick(250);
  useWakeLock();
  useLook(t.state?.config.look);
  const fullscreen = useFullscreen();
  const fullscreenButton =
    !fullscreen && document.fullscreenEnabled ? (
      <button type="button" className="btn quiet court-full" onClick={() => void toggleFullscreen()}>
        Full screen
      </button>
    ) : null;

  const setSaved = (next: Saved): void => {
    setSavedState(next);
    saveJson(key, next);
  };

  // ?demo=courts3|courts2: the day with fewer courts in use, on this screen only
  const fewer = useRef(demoCourtsFromLocation()).current;
  const now = t.now();
  // ?demo=lunch and the like: see projector/demoDay.ts
  const pretend = useRef(demoDayFromLocation()).current;
  const openedAt = useRef(now).current;
  let s = t.state && fewer ? demoCourts(t.state, fewer) : t.state;
  if (s && pretend) s = demoDay(s, pretend, openedAt);
  const round = s ? currentRound(s) : null;
  const owedHere = s ? owedScores(s).filter((o) => o.court === n) : [];
  const isOwed = (no: number): boolean => owedHere.some((o) => o.roundNo === no);
  const myGame = round?.games.find((g) => g.court === n) ?? null;

  const pending = saved.pending;
  let activeNo: number | null = null;
  if (pending) activeNo = pending.roundNo;
  else if (saved.focus !== null && isOwed(saved.focus)) activeNo = saved.focus;
  else if (round && myGame && isOwed(round.no)) activeNo = round.no;

  // Settle a sent score: the ack clears it, a refusal reopens the steppers with the reason.
  const queued = pending ? t.queue.some((q) => q.id === pending.id) : false;
  useEffect(() => {
    if (!pending || !s) return;
    const game = roundByNo(s, pending.roundNo)?.games.find((g) => g.court === n);
    const drafts = { ...saved.drafts };
    // the refusal first: a game that has a score by now may have someone else's
    if (t.refusal?.id === pending.id && t.lastAck?.id !== pending.id) {
      if (game?.score) {
        // the game is not owed any more, so the steppers stay shut: say what is in instead
        const hi = Math.max(game.score.a, game.score.b);
        const lo = Math.min(game.score.a, game.score.b);
        const text = `Not sent: this game already has ${hi}-${lo}. If that is wrong, ask an admin to correct it.`;
        delete drafts[String(pending.roundNo)];
        setSaved({ focus: null, drafts, pending: null, reason: null, clash: { text, rounds: s.rounds.length } });
      } else {
        // a round taken back (gone now, or started again): the numbers were for other players, so they go
        if (t.refusal.reason === TAKEN_BACK || !roundByNo(s, pending.roundNo)) delete drafts[String(pending.roundNo)];
        setSaved({ ...saved, drafts, pending: null, reason: t.refusal.reason });
      }
    } else if (t.lastAck?.id === pending.id || (!queued && game?.score)) {
      delete drafts[String(pending.roundNo)];
      setSaved({ focus: null, drafts, pending: null, reason: null });
    } else if (!queued && t.status === "open") {
      // Neither acked nor queued nor scored: it was refused before a reload.
      setSaved({ ...saved, pending: null, reason: "That score did not go through. Check it and send again." });
    }
  }, [pending, queued, t.lastAck, t.refusal, t.status, s]);

  // Forget numbers for rounds that are no longer owed (scored elsewhere, or voided).
  useEffect(() => {
    if (!s) return;
    const stale = Object.keys(saved.drafts).filter((no) => !isOwed(Number(no)) && pending?.roundNo !== Number(no));
    if (stale.length) {
      const drafts = { ...saved.drafts };
      for (const no of stale) delete drafts[no];
      setSaved({ ...saved, drafts });
    }
  }, [s?.seq]);

  const waiting = t.queue.length;
  const waitingNote =
    waiting > 0 ? (
      <p className="court-note">
        {waiting === 1 ? "1 score waiting to send" : `${waiting} scores waiting to send`}
        {t.status !== "open" ? ". It goes when the tablet is back online." : ""}
      </p>
    ) : null;

  if (!s) {
    return (
      <div className={`close court-screen on-court-${n}`}>
        <span className={"status-pill " + t.status}>{STATUS_WORDS[t.status]}</span>
        <header className={`court-band court-${n}`}>
          <h1>Court {n}</h1>
          {fullscreenButton}
        </header>
        {waitingNote}
        <p className="court-quiet">Connecting to the tournament</p>
      </div>
    );
  }

  // A court the day is not using says so and nothing else. A game still being played here, or a
  // score still owed from here, comes first: the court closes once those are done.
  if (n > s.config.courts && !owedHere.length && !pending && !(s.clock.phase === "playing" && myGame)) {
    return (
      <div className={`close court-screen court-closed on-court-${n}`}>
        <span className={"status-pill " + t.status}>{STATUS_WORDS[t.status]}</span>
        <p className="court-closed-line">Court {n} is closed for now</p>
      </div>
    );
  }

  const phase = phaseLine(s, now);
  const activeGame: GameRecord | null =
    activeNo !== null ? (roundByNo(s, activeNo)?.games.find((g) => g.court === n) ?? null) : null;
  // a game still being played, or one owed: a scored game's mini game is over, so between rounds
  // the banner goes (the final-day run of 18 Oct: Quickfire sat above Up next all through lunch)
  const shownGame: GameRecord | null = activeGame ?? (myGame && !myGame.score ? myGame : null);
  const event = shownGame ? eventById(s.config, shownGame.eventId) : null;
  const twist = twistNow(s, now);
  const curse = cursesNow(s, now).find((c) => c.court === n) ?? null;
  const onBreak = breakNow(s, now);
  const activeRound = activeNo !== null ? roundByNo(s, activeNo) : null;
  const olderOwed = owedHere.filter((o) => o.roundNo !== activeNo && o.roundNo !== round?.no);

  return (
    <div className={`close court-screen on-court-${n}`}>
      <span className={"status-pill " + t.status}>{STATUS_WORDS[t.status]}</span>
      {olderOwed.length > 0 && (
        <div className="court-owed-top">
          <Mascot pose="nag" className="court-host" />
          <div className="court-owed-list">
            {olderOwed.map((o) => (
              <div className="court-owed" key={o.roundNo}>
                <span>Round {o.roundNo}'s score is still needed. Enter it now.</span>
                <button
                  type="button"
                  className="btn"
                  disabled={!!pending}
                  onClick={() => {
                    setConfirming(false);
                    setSaved({ ...saved, focus: o.roundNo, reason: null });
                  }}
                >
                  Enter it
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <header className={`court-band court-${n}`}>
        <h1>Court {n}</h1>
        <div className="court-clock">
          <span>{phase.label}</span>
          {phase.time && <span className="court-count">{phase.time}</span>}
        </div>
        {fullscreenButton}
      </header>

      {onBreak && (
        <div className="court-twist court-break" role="status">
          <span className="court-twist-what">
            <strong>{onBreak.label}</strong>
            <span>{onBreak.over ? BREAK_OVER : onBreak.backAt}</span>
          </span>
          {!onBreak.over && onBreak.leftMs !== null && <span className="court-twist-count">{`${countdown(onBreak.leftMs)} to go`}</span>}
        </div>
      )}

      {twist?.on && (
        <div className="court-twist" role="status">
          <span className="court-twist-what">
            <strong>{twist.name}</strong>
            <span>Whole hall, then back to normal</span>
          </span>
          <span className="court-twist-count">{mmss(twist.leftMs)}</span>
        </div>
      )}

      {waitingNote}

      {saved.clash && saved.clash.rounds === s.rounds.length && (
        <p className="court-refused" role="status">{saved.clash.text}</p>
      )}

      {event && (
        <div className="court-event">
          <strong>{event.name}</strong>
          {eventChanges(s.config, event) && <span>{eventChanges(s.config, event)}</span>}
        </div>
      )}

      {activeGame && activeNo !== null ? (
        <Entry
          key={activeNo}
          state={s}
          faceOf={t.faceOf}
          bounty={activeRound ? bountiesOn(activeRound, n) : { a: 0, b: 0 }}
          curse={curse && curse.roundNo === activeNo ? curse : null}
          game={activeGame}
          roundNo={activeNo}
          isCurrent={activeNo === round?.no}
          now={now}
          draft={saved.drafts[String(activeNo)] ?? BLANK}
          pending={pending}
          reason={saved.reason}
          confirming={confirming}
          setConfirming={setConfirming}
          onDraft={(d) => setSaved({ ...saved, drafts: { ...saved.drafts, [String(activeNo)]: d }, reason: null })}
          onSend={(score) => {
            // the round's start goes with it: a round taken back and started again keeps its
            // number, and a score queued offline for the old draw must not land on the new one
            const startedAt = activeRound ? { startedAt: activeRound.startedAt } : {};
            const id = t.send({ type: "game.scored", roundNo: activeNo, court: n, score, ...startedAt }, { queue: true });
            setConfirming(false);
            setSaved({ ...saved, pending: { id, roundNo: activeNo, score }, reason: null, clash: null });
          }}
          backTo={activeNo !== round?.no && round && myGame && isOwed(round.no) ? round.no : null}
          onBack={() => setSaved({ ...saved, focus: null, reason: null })}
        />
      ) : (
        <ResultAndNext state={s} faceOf={t.faceOf} n={n} game={myGame} roundNo={round?.no ?? null} />
      )}
    </div>
  );
}

function Entry(props: {
  state: TournamentState;
  faceOf: FaceOf;
  bounty: { a: number; b: number };
  /** The curse running on this court in this round, if there is one. */
  curse: { side: "a" | "b"; leftMs: number; kind?: CurseKind; stepsOff?: PlayerId } | null;
  game: GameRecord;
  roundNo: number;
  isCurrent: boolean;
  now: number;
  draft: Draft;
  pending: Saved["pending"];
  reason: string | null;
  confirming: boolean;
  setConfirming: (on: boolean) => void;
  onDraft: (d: Draft) => void;
  onSend: (score: Score) => void;
  backTo: number | null;
  onBack: () => void;
}) {
  const { state, game, roundNo, draft, pending } = props;
  const rules = rulesFor(state.config, eventById(state.config, game.eventId));
  const left = msLeft(state, props.now);
  const clockOn = state.clock.phase === "playing" && state.clock.roundNo === roundNo;
  const timeUp = !clockOn || (left !== null && left <= 0);
  const capped = draft.capped ?? timeUp;
  const showSwitch = timeUp || draft.capped !== null;
  const score: Score = { a: draft.a, b: draft.b, capped };
  const problem = scoreProblem(score, rules);
  const blank = draft.a === 0 && draft.b === 0;
  const a = names(state, game.a);
  const b = names(state, game.b);
  const winners = draft.a > draft.b ? a : b;
  const hi = Math.max(draft.a, draft.b);
  const lo = Math.min(draft.a, draft.b);
  // Only call it capped when the clock really decided it; a finished game is a finished game.
  const toSend: Score = { ...score, capped: capped && !isFinished(hi, lo, rules) };
  const sending = !!pending;
  const locked = sending || props.confirming;
  const cursed = (side: "a" | "b"): string | null =>
    props.curse?.side === side ? `${curseLabel(state, game[side], props.curse)} ${mmss(props.curse.leftMs)}` : null;

  return (
    <section className="court-entry">
      <div className="court-entry-head">
        {props.isCurrent && timeUp && <Mascot pose="whistle" className="court-host" />}
        <h2>{props.isCurrent ? `Round ${roundNo}: enter the final score` : `Round ${roundNo}'s score`}</h2>
        {props.backTo !== null && (
          <button type="button" className="btn quiet" disabled={sending} onClick={props.onBack}>
            Back to round {props.backTo}
          </button>
        )}
      </div>
      <div className="court-steppers">
        <Stepper team={people(state, props.faceOf, game.a)} bounty={props.bounty.a} curse={cursed("a")} value={draft.a} disabled={locked} onChange={(v) => props.onDraft({ ...draft, a: v })} />
        <div className="court-v">v</div>
        <Stepper team={people(state, props.faceOf, game.b)} bounty={props.bounty.b} curse={cursed("b")} value={draft.b} disabled={locked} onChange={(v) => props.onDraft({ ...draft, b: v })} />
      </div>

      {showSwitch && (
        <label className="court-switch">
          <input
            type="checkbox"
            checked={capped}
            disabled={locked}
            onChange={(e) => props.onDraft({ ...draft, capped: e.target.checked })}
          />
          <span>Time ran out</span>
        </label>
      )}

      {props.reason && <p className="court-refused">Not saved: {props.reason}</p>}
      {/* nothing entered yet is not a problem: a plain line until the first point goes on */}
      <p className={"court-problem" + (blank ? " idle" : problem ? "" : " ok")}>
        {blank ? (timeUp ? "Enter the final score" : "Enter the final score when your game ends") : (problem ?? "Ready to send")}
      </p>

      {sending ? (
        <div className="court-sending">Sending {pending.score.a}-{pending.score.b}</div>
      ) : props.confirming ? (
        <div className="court-confirm">
          <p>
            Sure? {Math.max(draft.a, draft.b)}-{Math.min(draft.a, draft.b)} to {winners.join(" & ")}
            {toSend.capped ? ", time ran out" : ""}
          </p>
          <div className="court-actions">
            <button type="button" className="btn quiet" onClick={() => props.setConfirming(false)}>
              Back
            </button>
            <button type="button" className="btn accent" onClick={() => props.onSend(toSend)}>
              Yes, send it
            </button>
          </div>
        </div>
      ) : (
        <div className="court-actions">
          <button type="button" className="btn accent court-submit" disabled={!!problem} onClick={() => props.setConfirming(true)}>
            Submit
          </button>
        </div>
      )}
    </section>
  );
}

/** Why there is no preview of the next round: the true reason, not always the scores. */
function notDrawnWhy(state: TournamentState): string {
  if (availablePlayers(state).length < state.config.playersPerCourt) return "Not drawn yet: not enough people here";
  const round = currentRound(state);
  if (state.mode === "mexicano" && round && !allScoresIn(round)) return "Drawn when all scores are in";
  return "Not drawn yet";
}

function ResultAndNext({
  state,
  faceOf,
  n,
  game,
  roundNo,
}: {
  state: TournamentState;
  faceOf: FaceOf;
  n: number;
  game: GameRecord | null;
  roundNo: number | null;
}) {
  const next = state.draft?.courts.find((c) => c.court === n) ?? null;
  const nextEvent = next ? eventById(state.config, next.eventId) : null;
  let result: ReactNode;
  if (roundNo === null) {
    result = <p className="court-quiet">Before round 1</p>;
  } else if (!game) {
    result = <p className="court-quiet">No game on this court this round</p>;
  } else if (game.voided) {
    result = (
      <>
        <h2>Round {roundNo}</h2>
        <p className="court-quiet">This game was voided{game.voidReason ? `: ${game.voidReason}` : ""}</p>
      </>
    );
  } else if (game.score) {
    const aWon = game.score.a > game.score.b;
    const winners = people(state, faceOf, aWon ? game.a : game.b);
    const losers = people(state, faceOf, aWon ? game.b : game.a);
    result = (
      <>
        <h2>Round {roundNo} result</h2>
        <div className="court-result-score">
          {Math.max(game.score.a, game.score.b)}-{Math.min(game.score.a, game.score.b)}
        </div>
        <p className="court-result-who">
          <Team team={winners} /> <span>beat</span> <Team team={losers} />
          {game.score.capped ? <span>, time ran out</span> : null}
        </p>
      </>
    );
  } else {
    result = <p className="court-quiet">Waiting for the score</p>;
  }

  // Up next comes first and big: the four walking up to this court are the ones reading it
  return (
    <div className="court-after">
      <section className="court-card court-next">
        <h2>Up next on this court</h2>
        {next ? (
          <>
            <div className="court-next-teams">
              <Team team={people(state, faceOf, next.a)} />
              <span className="court-v">v</span>
              <Team team={people(state, faceOf, next.b)} />
            </div>
            {nextEvent && <p className="court-quiet">{nextEvent.name}</p>}
            {!state.config.autoAdvance && (
              <p className="court-quiet">The next round starts when an admin says, once the courts are ready</p>
            )}
          </>
        ) : state.draft ? (
          <p className="court-quiet">No game on this court next round</p>
        ) : gamesDone(state) ? (
          <p className="court-quiet">No more games: that was the last round</p>
        ) : (
          <p className="court-quiet">{notDrawnWhy(state)}</p>
        )}
      </section>
      <section className="court-card court-result">{result}</section>
    </div>
  );
}
